// Topology — 視覺總覽。
//
// Renders the full tenant hierarchy (account → 終端客戶 → 案場 →
// 建築物 → 樓層 → 地點) as an interactive React Flow graph, with a
// toggle to overlay the route × location cross-references on top.
// Hover (desktop) / tap (mobile) any node to surface a semi-transparent
// detail panel anchored to the cursor / corner.
//
// Backend: GET /v1/tenants/topology — flat collections, layout done here.
//
// Layout strategy: a hand-rolled column tree. Each tier (tenant /
// client / site / building / floor / location) lives in its own
// vertical band; children are vertically distributed inside the band
// directly underneath their parent. This keeps parent-child edges as
// short downward lines and avoids the wandering of force-directed
// layouts. Route edges (when toggled on) cross between location
// nodes in a different colour so visual hierarchy stays legible.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ReactFlow, Background, Controls, MiniMap,
  type Node, type Edge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Building2, Users as UsersIcon, MapPin, Layers, ScanLine, Route as RouteIcon,
  Workflow, AlertTriangle, Loader2, ChevronsDownUp, ChevronsUpDown, RefreshCw, X,
} from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import EmptyState from "../components/EmptyState.js";
import TenantPickerEmptyState from "../components/TenantPickerEmptyState.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useAiMode } from "../AiModeContext.js";
import ChatBubble, { type ActivePlanInfo, type ActiveDrawerFormInfo } from "../chat/ChatBubble.js";
import AiConsoleDrawer from "../chat/AiConsoleDrawer.js";
import type { DrawerView } from "../chat/AiConsoleIconRow.js";
import { useAiConsoleSlot } from "../chat/AiConsoleSlotContext.js";
import { PlanPanel, type OpResult } from "../chat/PlanModal.js";
import DrawerFormHost, { type DrawerFormOutcome } from "../chat/DrawerFormHost.js";
import {
  fetchTopology, type TopologyResponse,
  type TopologyClient, type TopologySite, type TopologyBuilding,
  type TopologyFloor, type TopologyLocation, type TopologyRoute,
} from "../lib/topologyApi.js";

// Vertical lane positions per entity tier. Wider lanes for tiers with
// more children expected. Tweak to taste; React Flow's auto-fit handles
// the absolute zoom.
// Route lane sits BETWEEN floor and location so the overlay's
// dashed route→location edges read left-to-right (same direction
// as the structural backbone), instead of curling backwards over the
// location column. Visual order: floor → route → location.
const LANE_X = {
  tenant: 0,
  client: 320,
  site: 640,
  building: 960,
  floor: 1280,
  route: 1600,
  location: 1920,
};
const NODE_VGAP = 80;

type NodeKind = "tenant" | "client" | "site" | "building" | "floor" | "location" | "route";

interface NodeData extends Record<string, unknown> {
  kind: NodeKind;
  label: string;
  sublabel?: string;
  /** Original entity payload — surfaced to the info panel via the
   *  selected node's data field. */
  entity: unknown;
  /** True when this node has children that could be expanded /
   *  collapsed. Used by the click handler to decide whether to toggle
   *  expand or just show info. */
  hasChildren?: boolean;
  /** True when this node IS in the highlight set (AM-3 wires apply-
   *  complete to set this). Renders an accent ring around the node. */
  highlighted?: boolean;
}

function parseIdList(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

/** Shared canvas — used by both AI mode (inside flex column) and
 *  professional mode (full-height single panel). Keeps the
 *  ReactFlow + Background + Controls + MiniMap + InfoPanel
 *  configuration in one place. */
function TopologyCanvas(props: {
  graph: { nodes: Node[]; edges: Edge[] };
  data: TopologyResponse;
  selected: NodeData | null;
  setSelected: (n: NodeData | null) => void;
  toggleNode: (id: string) => void;
  /** AM-5c — changes when the focus URL param changes; used as the
   *  React key on ReactFlow so the canvas re-mounts and `fitView`
   *  refits to the new visible-node set (focused subtree). Without
   *  the remount the original camera position would stay, leaving the
   *  operator looking at empty space where filtered branches used
   *  to be. */
  fitKey: string;
}): React.ReactElement {
  const { graph, data, selected, setSelected, toggleNode, fitKey } = props;
  return (
    <>
      <ReactFlow
        key={fitKey}
        nodes={graph.nodes}
        edges={graph.edges}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        fitView
        fitViewOptions={{ padding: 0.15 }}
        proOptions={{ hideAttribution: true }}
        onNodeClick={(_e, n) => {
          const nd = n.data as NodeData;
          setSelected(nd);
          if (nd.hasChildren) toggleNode(n.id);
        }}
        onNodeMouseEnter={(_e, n) => setSelected(n.data as NodeData)}
        onPaneClick={() => setSelected(null)}
      >
        <Background gap={24} size={1} color="#1f2937" />
        <Controls
          showInteractive={false}
          className="!bg-bg-raised !border-border !text-text-secondary"
        />
        <MiniMap
          pannable zoomable
          maskColor="rgba(0,0,0,0.6)"
          className="!bg-bg-raised !border-border"
          nodeColor={(n) => nodeColor((n.data as NodeData).kind)}
        />
      </ReactFlow>
      {selected && (
        <InfoPanel
          node={selected}
          topology={data}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

/** Build a flat child→parent ID map across every tier so plan-apply-
 *  complete can walk a new leaf back to the tenant root for expand. */
function buildParentMap(d: TopologyResponse): Map<string, string> {
  const m = new Map<string, string>();
  for (const c of d.clients) m.set(c.id, d.tenant.id);
  for (const s of d.sites) m.set(s.id, s.client_id);
  for (const b of d.buildings) m.set(b.id, b.site_id);
  for (const f of d.floors) {
    if (f.building_id) m.set(f.id, f.building_id);
    else m.set(f.id, `__site:${f.site_id}`);
  }
  for (const cp of d.locations) {
    if (cp.floor_id) m.set(cp.id, cp.floor_id);
    else m.set(cp.id, `__floor:site:${cp.site_id}`);
  }
  return m;
}

export default function Topology(): React.ReactElement {
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ok"; data: TopologyResponse }
    | { kind: "error"; message: string }
  >({ kind: "loading" });
  const [showRoutes, setShowRoutes] = useState(true);
  const [selected, setSelected] = useState<NodeData | null>(null);

  // URL-driven expand / highlight state — spec
  // docs/specs/ai-mode-operations-console.md §4.5. AM-3 hooks plan-
  // apply completion by navigating with ?expanded=<ids>&highlight=<ids>;
  // here we just read + render so the URL is the single source of truth
  // and survives reload + deep-link.
  const [searchParams, setSearchParams] = useSearchParams();
  const expandedFromUrl = useMemo(
    () => new Set(parseIdList(searchParams.get("expanded"))),
    [searchParams],
  );
  const highlighted = useMemo(
    () => new Set(parseIdList(searchParams.get("highlight"))),
    [searchParams],
  );
  // AM-5c focus filter — narrows the topology canvas to the focused
  // ids + their ancestor chain. AI emits propose_focus_topology with
  // the ids the conversation is about; the page restricts node
  // emission so unrelated branches don't crowd the canvas. Focused
  // ids also flow into `highlighted` below so they get the amber ring
  // treatment they'd have from a plan-apply success.
  const focused = useMemo(
    () => new Set(parseIdList(searchParams.get("focus"))),
    [searchParams],
  );
  const focusReason = searchParams.get("focus_reason") ?? "";
  // AM-5a drawer view state — URL ?drawer=topology|plan|closed.
  // Spec docs/specs/ai-mode-operations-console.md §4.5.
  // Default closed so chat reads full-width on first visit; operator
  // opens via the icon row in PageHeader actions. AM-5b will auto-
  // open to "plan" when AI emits propose_plan; AM-5c will auto-open
  // to "topology" when AI emits propose_focus_topology.
  const drawerView = useMemo<DrawerView>(() => {
    const raw = searchParams.get("drawer");
    // Whitelist must include every DrawerView value — when AM-7a
    // added "form" to the type the validator wasn't updated, so
    // setDrawerView("form") wrote ?drawer=form but the memo read
    // it back as "closed" and the drawer stayed shut. Operator
    // saw the「準備新增表單」chip but no form ever appeared.
    if (raw === "topology" || raw === "plan" || raw === "form") return raw;
    return "closed";
  }, [searchParams]);
  const setDrawerView = useCallback((next: DrawerView) => {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      if (next === "closed") out.delete("drawer");
      else out.set("drawer", next);
      return out;
    }, { replace: true });
  }, [setSearchParams]);
  // Default expansion when the URL says nothing: tenant + every client
  // (top 2 tiers visible, sites/buildings/floors/locations collapsed).
  // Computed lazily from loaded data so we never auto-expand before
  // we know what exists.
  //
  // AM-5c: when a focus set is active, auto-expand the full ancestor
  // chain of every focused id so the focused nodes are actually
  // visible (otherwise a collapsed parent would hide them and the
  // operator would see an empty canvas while AI says 「我已標記出 X
  // 案場相關節點」).
  const effectiveExpanded = useMemo(() => {
    const base = expandedFromUrl.size > 0
      ? new Set(expandedFromUrl)
      : (() => {
          if (state.kind !== "ok") return new Set<string>();
          const defaults = new Set<string>();
          defaults.add(state.data.tenant.id);
          for (const c of state.data.clients) defaults.add(c.id);
          return defaults;
        })();
    if (focused.size > 0 && state.kind === "ok") {
      const parents = buildParentMap(state.data);
      for (const id of focused) {
        let cur: string | undefined = parents.get(id);
        while (cur) {
          base.add(cur);
          cur = parents.get(cur);
        }
        base.add(state.data.tenant.id); // always show the root
      }
    }
    return base;
  }, [expandedFromUrl, state, focused]);

  const isExpanded = useCallback(
    (id: string) => effectiveExpanded.has(id),
    [effectiveExpanded],
  );

  const setExpanded = useCallback((next: Set<string>) => {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      if (next.size === 0) out.delete("expanded");
      else out.set("expanded", Array.from(next).join(","));
      return out;
    }, { replace: true });
  }, [setSearchParams]);

  const toggleNode = useCallback((id: string) => {
    const next = new Set(effectiveExpanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  }, [effectiveExpanded, setExpanded]);

  const expandAll = useCallback(() => {
    if (state.kind !== "ok") return;
    const all = new Set<string>();
    all.add(state.data.tenant.id);
    for (const c of state.data.clients) all.add(c.id);
    for (const s of state.data.sites) all.add(s.id);
    for (const b of state.data.buildings) all.add(b.id);
    for (const f of state.data.floors) all.add(f.id);
    setExpanded(all);
  }, [state, setExpanded]);

  const collapseAll = useCallback(() => {
    // "Collapse all" means "leave only tenant visible" — easier mental
    // model than literally zero (which would render an empty canvas).
    if (state.kind !== "ok") return;
    setExpanded(new Set([state.data.tenant.id]));
  }, [state, setExpanded]);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const data = await fetchTopology(tenantParam);
      setState({ kind: "ok", data });
    } catch (e) {
      setState({ kind: "error", message: (e as Error).message });
    }
  }, [tenantParam]);

  useEffect(() => { void load(); }, [load]);

  // AI 模式 detection — spec §4.5. When in AI mode this page becomes
  // the "AI 導覽" page: topology canvas on top + inline ChatBubble on
  // the bottom + apply-complete callback that drives URL ?expanded= +
  // ?highlight= so newly-created nodes auto-surface in the graph.
  const { mode } = useAiMode();
  const isAiMode = mode === "ai";

  // AM-5b plan-in-drawer state. ChatBubble owns the activePlan state
  // machine internally (propose_plan SSE event → set, plan close →
  // clear) and emits transitions via onActivePlanChange. We mirror
  // that here so the drawer can host PlanPanel and the IconRow can
  // light up its FileText button.
  const [activePlan, setActivePlan] = useState<ActivePlanInfo | null>(null);
  // AM-7 drawer-form state. Same pattern as activePlan — ChatBubble
  // owns the source, here we mirror for drawer rendering + auto-open.
  const [activeDrawerForm, setActiveDrawerForm] = useState<ActiveDrawerFormInfo | null>(null);
  // Track previous activePlan presence to detect the null → non-null
  // transition and auto-open the drawer to "plan" view. Without this
  // guard, switching drawer view back to "topology" while a plan was
  // still active would loop straight back to plan on the next render.
  const prevPlanRef = useRef<boolean>(false);
  useEffect(() => {
    if (activePlan && !prevPlanRef.current) {
      setDrawerView("plan");
    }
    prevPlanRef.current = !!activePlan;
  }, [activePlan, setDrawerView]);

  // AM-7 — auto-open drawer to "form" view on null → non-null
  // transition. Same one-shot guard as activePlan so the operator can
  // manually switch away mid-form (e.g. peek at topology) without
  // getting bounced back.
  const prevFormRef = useRef<boolean>(false);
  useEffect(() => {
    if (activeDrawerForm && !prevFormRef.current) {
      setDrawerView("form");
    }
    prevFormRef.current = !!activeDrawerForm;
  }, [activeDrawerForm, setDrawerView]);

  // AM-7 — when the drawer form completes successfully we want to
  // surface the new entity in the topology canvas, mirroring what
  // plan-apply does. We don't have the parent-chain expand logic
  // wired in here because the form only creates ONE entity at a time;
  // a refetch + URL ?expanded= / ?highlight= update is enough.
  const handleDrawerFormComplete = useCallback(async (outcome: DrawerFormOutcome) => {
    // Cancelled / issued_binding_code → fully close the drawer. There's
    // no entity to surface in topology view; for issued_binding_code the
    // QR card already lives in the chat itself so the operator wants to
    // see chat, not the topology canvas behind a half-open drawer.
    if (outcome.kind === "cancelled" || outcome.kind === "issued_binding_code") {
      setDrawerView("closed");
      return;
    }
    // create_* outcomes → switch to topology view to surface the new
    // node + refetch + highlight + auto-expand ancestor chain.
    setDrawerView("topology");
    let fresh: TopologyResponse;
    try { fresh = await fetchTopology(tenantParam); }
    catch { return; }
    setState({ kind: "ok", data: fresh });
    const parentOf = buildParentMap(fresh);
    const expandedNext = new Set(effectiveExpanded);
    const highlightNext = new Set<string>(highlighted);
    highlightNext.add(outcome.entity_id);
    let cur: string | undefined = parentOf.get(outcome.entity_id);
    while (cur) {
      if (!cur.startsWith("__")) expandedNext.add(cur);
      cur = parentOf.get(cur);
    }
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set("expanded", Array.from(expandedNext).join(","));
      out.set("highlight", Array.from(highlightNext).join(","));
      return out;
    }, { replace: true });
  }, [tenantParam, effectiveExpanded, highlighted, setSearchParams, setDrawerView]);

  // Push drawer-view controls up to the global Header on desktop so
  // they sit on the same row as the AI / 專業模式 toggle. The Header
  // hides this slot on mobile (Header bar too tight); the page
  // renders the same IconRow inline below the chat on mobile.
  //
  // Only push once the topology data is loaded — pre-data states
  // (tenant-picker / loading / error) don't render the drawer, so
  // an IconRow in Header would dangle.
  const { setSlot } = useAiConsoleSlot();
  const dataReady = state.kind === "ok"
    && (!session.super_admin || !!tenantParam);
  useEffect(() => {
    if (!isAiMode || !dataReady) {
      setSlot(null);
      return;
    }
    setSlot({
      view: drawerView,
      setView: setDrawerView,
      planSupported: !!activePlan,
      planPending: !!activePlan && drawerView !== "plan",
    });
    return () => setSlot(null);
  }, [isAiMode, dataReady, drawerView, setDrawerView, activePlan, setSlot]);

  /** Handler for ChatBubble's onPlanApplyComplete in inline mode.
   *  Walks the OpResults for `kind: "success" && created_id`, refetches
   *  topology, derives the ancestor chain for each new id, and writes
   *  the result into the URL params so AM-2's expand mechanism does
   *  the visual reveal. Failed/skipped ops are ignored — they don't
   *  produce new nodes to surface. */
  const handlePlanApplyComplete = useCallback(async (results: OpResult[]) => {
    const newIds = results
      .filter((r): r is Extract<OpResult, { kind: "success" }> => r.kind === "success")
      .map((r) => r.created_id)
      .filter((id): id is string => typeof id === "string" && id.length > 0);
    if (newIds.length === 0) return;
    // AM-5b: at least one op succeeded → switch drawer from plan view
    // (where the operator was reviewing the results) to topology view
    // so the highlight ring on the freshly-created nodes is visible
    // without an extra click on the IconRow.
    setDrawerView("topology");

    // Refetch first so the just-created nodes exist in our local
    // topology data; otherwise ancestor lookup misses them.
    let fresh: TopologyResponse;
    try { fresh = await fetchTopology(tenantParam); }
    catch { return; /* fail-soft */ }
    setState({ kind: "ok", data: fresh });

    // Build a parent-id map across every tier so we can walk leaves
    // back to the tenant root. For each new id, accumulate the full
    // ancestor chain into `expandedNext`; the new id itself goes into
    // `highlightNext`. Then URL-sync both at once.
    const parentOf = buildParentMap(fresh);
    const expandedNext = new Set(effectiveExpanded);
    const highlightNext = new Set<string>(highlighted);
    for (const id of newIds) {
      highlightNext.add(id);
      let cur: string | undefined = parentOf.get(id);
      while (cur) {
        expandedNext.add(cur);
        cur = parentOf.get(cur);
      }
    }
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      out.set("expanded", Array.from(expandedNext).join(","));
      out.set("highlight", Array.from(highlightNext).join(","));
      return out;
    }, { replace: true });
  }, [tenantParam, effectiveExpanded, highlighted, setSearchParams, setDrawerView]);

  // Build React Flow nodes + edges from the flat collections. The bulk
  // of the layout work is in placeColumn() — assigns each entity a
  // vertical slot inside its tier lane, anchored directly under its
  // parent so the resulting tree reads top-to-bottom.
  //
  // Collapse: when a parent isn't in `isExpanded`, its subtree is
  // skipped during node emission AND its subtreeHeight collapses to 1
  // so the layout reflows tight (no wasted vertical space).
  // AM-5c — when a focus set is active, also light up focused ids
  // with the same amber ring `highlighted` produces, so the operator
  // sees them as the answer to「AI 在說的那些」without a separate
  // highlight URL param.
  const effectiveHighlight = useMemo(() => {
    if (focused.size === 0) return highlighted;
    const merged = new Set(highlighted);
    for (const id of focused) merged.add(id);
    return merged;
  }, [focused, highlighted]);

  const graph = useMemo(() => {
    if (state.kind !== "ok") return { nodes: [], edges: [] };
    return buildGraph(state.data, {
      showRoutes, isExpanded,
      highlighted: effectiveHighlight,
      focusSet: focused.size > 0 ? focused : undefined,
    });
  }, [state, showRoutes, isExpanded, effectiveHighlight, focused]);

  // Page title swaps with the active mode (spec §4.5). 視覺總覽 → AI
  // 導覽 in AI mode to reflect the embedded chat panel below.
  const pageTitle = isAiMode ? "AI 導覽" : "視覺總覽";
  const pageDescription = isAiMode
    ? "結構圖 + 內嵌 AI 對話 — 跟 AI 說你想做什麼，套用完成自動展開受影響節點"
    : "案場層級結構 + 路線交叉視覺化 — 點擊有子層的節點可展開 / 收合";

  if (session.super_admin && !tenantParam) {
    return (
      <>
        {!isAiMode && <PageHeader title={pageTitle} description={pageDescription} />}
        <TenantPickerEmptyState />
      </>
    );
  }

  if (state.kind === "loading") {
    return (
      <>
        {!isAiMode && <PageHeader title={pageTitle} description={pageDescription} />}
        <div className="flex items-center justify-center min-h-[60vh] gap-2 text-text-secondary">
          <Loader2 className="h-5 w-5 animate-spin" /> 載入中…
        </div>
      </>
    );
  }

  if (state.kind === "error") {
    return (
      <>
        {!isAiMode && <PageHeader title={pageTitle} description={pageDescription} />}
        <EmptyState
          icon={AlertTriangle}
          title="載入失敗"
          description={state.message}
          action={<button onClick={() => void load()} className="btn-primary text-sm">再試一次</button>}
        />
      </>
    );
  }

  const { data } = state;
  const totalNodes =
    1 + data.clients.length + data.sites.length + data.buildings.length +
    data.floors.length + data.locations.length + (showRoutes ? data.routes.length : 0);

  // Topology view actions (expand / collapse / reload / routes
  // toggle) — bundled into a snippet that's reused both as PageHeader
  // actions in professional mode and as drawer headerExtras in AI
  // mode (where the page strips PageHeader entirely).
  const clearFocus = () => {
    setSearchParams((prev) => {
      const out = new URLSearchParams(prev);
      out.delete("focus");
      out.delete("focus_reason");
      return out;
    }, { replace: true });
  };
  const topologyActions = (
    <>
      {focused.size > 0 && (
        <span
          className="inline-flex items-center gap-1 rounded-md border border-accent/50 bg-accent/10 px-2 py-1 text-2xs text-accent"
          title={`AI 已將結構圖聚焦到 ${focused.size} 個節點（與其上層）。點 X 清除`}
        >
          <span className="truncate max-w-[160px]">聚焦：{focusReason || `${focused.size} 個節點`}</span>
          <button
            type="button"
            onClick={clearFocus}
            aria-label="清除聚焦"
            className="inline-flex items-center justify-center h-4 w-4 rounded hover:bg-accent/20"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      )}
      <button
        type="button"
        onClick={expandAll}
        className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-surface hover:bg-bg-inset px-2 py-1 text-2xs text-text-secondary"
        title="全部展開"
      >
        <ChevronsUpDown className="h-3.5 w-3.5" /> 展開
      </button>
      <button
        type="button"
        onClick={collapseAll}
        className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-surface hover:bg-bg-inset px-2 py-1 text-2xs text-text-secondary"
        title="全部收合（保留 tenant）"
      >
        <ChevronsDownUp className="h-3.5 w-3.5" /> 收合
      </button>
      <button
        type="button"
        onClick={() => void load()}
        className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-surface hover:bg-bg-inset px-2 py-1 text-2xs text-text-secondary"
        title="重新載入"
      >
        <RefreshCw className="h-3.5 w-3.5" />
      </button>
      <label className="inline-flex items-center gap-2 text-xs text-text-secondary cursor-pointer select-none ml-2">
        <input
          type="checkbox"
          checked={showRoutes}
          onChange={(e) => setShowRoutes(e.target.checked)}
          className="accent-accent"
        />
        連線
      </label>
    </>
  );

  // AI mode: strip PageHeader entirely (title/description redundant —
  // chat panel already orients the operator); render edge-to-edge
  // without card chrome so the chat fills the full container. Header
  // hosts the drawer icon row via AiConsoleSlotContext on desktop;
  // mobile gets an inline IconRow row above the chat panel.
  //
  // Spec docs/specs/ai-mode-operations-console.md §4.5 (post-AM-5a
  // refinement: drop title / drop card wrapper).
  if (isAiMode) {
    // Break out of AppLayout's `mx-auto max-w-[1400px] px-* py-6`
    // wrapper so the chat truly fills the content area (header to
    // viewport bottom, sidebar to right edge of the max-w container).
    // Negative margins match the wrapper's padding values exactly.
    return (
      <div className="h-full flex flex-col bg-bg-base">
        {/* IconRow lives in the global Header (desktop) and inside the
            ChatBubble header's right cluster (mobile). No standalone
            strip here. AppLayout strips its padded wrapper for this
            route + mode combo (fullBleed branch) so `h-full` actually
            fills the main flex column without overflow / body scroll. */}
        <div className="flex flex-row flex-1 min-h-0">
          <div className="flex-1 min-w-0 relative">
            <ChatBubble
              variant="inline"
              onPlanApplyComplete={handlePlanApplyComplete}
              planHost="external"
              onActivePlanChange={setActivePlan}
              onActiveDrawerFormChange={setActiveDrawerForm}
              onDrawerFormComplete={handleDrawerFormComplete}
            />
          </div>
          <AiConsoleDrawer
            view={drawerView}
            onChangeView={setDrawerView}
            headerExtras={drawerView === "topology" ? topologyActions : undefined}
          >
            {drawerView === "topology" && (
              <TopologyCanvas
                graph={graph}
                data={data}
                selected={selected}
                setSelected={setSelected}
                toggleNode={toggleNode}
                fitKey={searchParams.get("focus") ?? "all"}
              />
            )}
            {drawerView === "plan" && (
              activePlan ? (
                // AM-5b plan-in-drawer. PlanPanel takes the activePlan
                // straight from ChatBubble's state machine and renders
                // the same Header / op list / Footer + apply loop the
                // floating modal would have. Closing via the X button
                // routes back through ChatBubble.handlePlanClose so the
                // AI thread receives the tool_result on dismiss.
                <div className="flex flex-col h-full">
                  <PlanPanel
                    plan={activePlan.plan}
                    tenantId={activePlan.tenantId}
                    onClose={activePlan.onClose}
                  />
                </div>
              ) : (
                <div className="p-4 text-xs text-text-muted leading-relaxed">
                  目前沒有待確認的計畫。AI 提出新計畫時，這裡會自動顯示。
                </div>
              )
            )}
            {drawerView === "form" && (
              // AM-7 — drawer-hosted create form. ChatBubble holds the
              // activeDrawerForm state machine; we pass `info.onComplete`
              // to DrawerFormHost so save / cancel routes back through
              // ChatBubble.handleDrawerFormComplete → tool_result.
              <DrawerFormHost
                formKind={activeDrawerForm?.kind ?? null}
                formCallId={activeDrawerForm?.callId ?? null}
                prefillJson={activeDrawerForm ? JSON.stringify(activeDrawerForm.prefill) : null}
                reason={activeDrawerForm?.reason ?? null}
                tenantId={activeDrawerForm?.tenantId ?? null}
                onComplete={(outcome) => activeDrawerForm?.onComplete(outcome)}
              />
            )}
          </AiConsoleDrawer>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title={pageTitle}
        description={pageDescription}
        actions={
          <div className="flex items-center gap-2">
            {topologyActions}
          </div>
        }
      />

      {totalNodes <= 1 ? (
        <EmptyState
          icon={Workflow}
          title="這個帳戶還沒有任何結構資料"
          description="請先到「終端客戶」「案場」「地點」等頁建立資料後再回來。"
        />
      ) : (
        <div className="relative w-full h-[calc(100vh-220px)] min-h-[480px] rounded-xl border border-border bg-bg-base overflow-hidden">
          <TopologyCanvas
            graph={graph}
            data={data}
            selected={selected}
            setSelected={setSelected}
            toggleNode={toggleNode}
            fitKey={searchParams.get("focus") ?? "all"}
          />
        </div>
      )}
    </>
  );
}

// ─── graph construction ────────────────────────────────────────────────

function buildGraph(
  d: TopologyResponse,
  opts: {
    showRoutes: boolean;
    isExpanded: (id: string) => boolean;
    highlighted: ReadonlySet<string>;
    /** AM-5c — when set, restrict node emission to ids in the focus
     *  set OR ancestors of focused ids. Unrelated branches are not
     *  rendered so the operator can concentrate on the conversation's
     *  subject. Filtering happens post-layout — the layout pass places
     *  the full tree, then we drop nodes outside the visible set; React
     *  Flow's `fitView` auto-zooms into the remaining content so visual
     *  gaps from filtered branches are off-screen. */
    focusSet?: ReadonlySet<string>;
  },
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const { isExpanded, highlighted, focusSet } = opts;

  // Index for fast parent → children walks during placement.
  const sitesByClient = group(d.sites, (s) => s.client_id);
  const buildingsBySite = group(d.buildings, (b) => b.site_id);
  const floorsByBuilding = group(d.floors, (f) => f.building_id ?? `__site:${f.site_id}`);
  const locationsByFloor = group(d.locations, (c) => c.floor_id ?? `__site:${c.site_id}`);
  const subtreeCtx: SubtreeCtx = { buildingsBySite, floorsByBuilding, locationsByFloor, isExpanded };

  // Helper: short suffix on a node's sublabel showing collapsed-child
  // count + cue. Only added when the node is currently collapsed.
  const collapsedSuffix = (n: number, unit: string) =>
    n > 0 ? `${n} ${unit}（已收合，點擊展開）` : null;

  // Place column-by-column, left to right. Each child's Y is the
  // running offset within its parent's reserved vertical span.
  const tenantY = 0;
  const tenantExpanded = isExpanded(d.tenant.id);
  const tenantChildCount = d.clients.length;
  nodes.push(makeNode(
    "tenant", d.tenant.id,
    LANE_X.tenant, tenantY,
    {
      kind: "tenant",
      label: d.tenant.name ?? "本帳戶",
      sublabel: tenantExpanded
        ? d.tenant.slug ?? undefined
        : collapsedSuffix(tenantChildCount, "個客戶") ?? d.tenant.slug ?? undefined,
      entity: d.tenant,
      hasChildren: tenantChildCount > 0,
      highlighted: highlighted.has(d.tenant.id),
    },
  ));

  // If tenant is collapsed, stop here — no clients / downstream tiers
  // render. This is the "collapse all" idle state.
  if (tenantExpanded) {
    let nextClientY = 0;
    for (const client of d.clients) {
      const clientChildren = sitesByClient.get(client.id) ?? [];
      const clientExpanded = isExpanded(client.id);
      const clientSubtreeHeight = clientExpanded
        ? Math.max(subtreeHeight(clientChildren, subtreeCtx), 1)
        : 1;
      const clientY = nextClientY + (clientSubtreeHeight - 1) * NODE_VGAP / 2;
      nodes.push(makeNode("client", client.id, LANE_X.client, clientY, {
        kind: "client",
        label: client.name,
        sublabel: clientExpanded
          ? `${client.site_count} 個案場`
          : collapsedSuffix(client.site_count, "個案場") ?? undefined,
        entity: client,
        hasChildren: clientChildren.length > 0,
        highlighted: highlighted.has(client.id),
      }));
      edges.push(makeEdge(d.tenant.id, client.id, "tenant-client"));

      if (clientExpanded) {
        // Sites under this client
        let siteY = nextClientY;
        for (const site of clientChildren) {
          const siteExpanded = isExpanded(site.id);
          const siteSubtree = siteExpanded
            ? Math.max(subtreeHeight([site], subtreeCtx), 1)
            : 1;
          const siteCenter = siteY + (siteSubtree - 1) * NODE_VGAP / 2;
          const siteHasChildren =
            (buildingsBySite.get(site.id)?.length ?? 0) > 0 ||
            (floorsByBuilding.get(`__site:${site.id}`)?.length ?? 0) > 0 ||
            (locationsByFloor.get(`__site:${site.id}`)?.length ?? 0) > 0;
          nodes.push(makeNode("site", site.id, LANE_X.site, siteCenter, {
            kind: "site",
            label: site.name,
            sublabel: site.client_name ?? undefined,
            entity: site,
            hasChildren: siteHasChildren,
            highlighted: highlighted.has(site.id),
          }));
          edges.push(makeEdge(client.id, site.id, "client-site"));

          if (siteExpanded) {
            // Buildings — site may have zero (legacy single-floor flat sites)
            // in which case floors hang directly under the site.
            const buildings = buildingsBySite.get(site.id) ?? [];
            const synthBuildingKey = `__site:${site.id}`;
            const orphanFloors = checkOrphanFloorBucket(floorsByBuilding, synthBuildingKey);
            const buildingTier: { id: string; nodeBuilding: TopologyBuilding | null }[] =
              buildings.length === 0 && orphanFloors.length === 0
                ? []
                : buildings.length === 0
                ? [{ id: synthBuildingKey, nodeBuilding: null }]
                : [
                    ...buildings.map((b) => ({ id: b.id, nodeBuilding: b })),
                    ...(orphanFloors.length > 0 ? [{ id: synthBuildingKey, nodeBuilding: null }] : []),
                  ];

            let buildingY = siteY;
            for (const b of buildingTier) {
              const buildingExpanded = b.nodeBuilding ? isExpanded(b.id) : true; // synth bucket always expanded
              const bSubtree = buildingExpanded
                ? Math.max(subtreeHeightFromBuilding(b.id, subtreeCtx), 1)
                : 1;
              const bCenter = buildingY + (bSubtree - 1) * NODE_VGAP / 2;
              const bChildCount = (floorsByBuilding.get(b.id) ?? []).length;
              if (b.nodeBuilding) {
                nodes.push(makeNode("building", b.id, LANE_X.building, bCenter, {
                  kind: "building",
                  label: b.nodeBuilding.name,
                  sublabel: buildingExpanded
                    ? undefined
                    : collapsedSuffix(bChildCount, "個樓層") ?? undefined,
                  entity: b.nodeBuilding,
                  hasChildren: bChildCount > 0,
                  highlighted: highlighted.has(b.id),
                }));
                edges.push(makeEdge(site.id, b.id, "site-building"));
              }

              if (buildingExpanded) {
                // Floors under this building (or under the synth bucket)
                const floors = floorsByBuilding.get(b.id) ?? [];
                let floorY = buildingY;
                for (const floor of floors) {
                  const floorExpanded = isExpanded(floor.id);
                  const cps = locationsByFloor.get(floor.id) ?? [];
                  const fSubtree = floorExpanded ? Math.max(cps.length, 1) : 1;
                  const fCenter = floorY + (fSubtree - 1) * NODE_VGAP / 2;
                  nodes.push(makeNode("floor", floor.id, LANE_X.floor, fCenter, {
                    kind: "floor",
                    label: floor.display_label ?? floor.name,
                    sublabel: floorExpanded
                      ? floor.name
                      : collapsedSuffix(cps.length, "個地點") ?? floor.name,
                    entity: floor,
                    hasChildren: cps.length > 0,
                    highlighted: highlighted.has(floor.id),
                  }));
                  edges.push(makeEdge(b.nodeBuilding ? b.id : site.id, floor.id, "building-floor"));

                  if (floorExpanded) {
                    // Locations under this floor.
                    let cpY = floorY;
                    for (const cp of cps) {
                      nodes.push(makeNode("location", cp.id, LANE_X.location, cpY, {
                        kind: "location",
                        label: cp.label ?? cp.code ?? cp.id.slice(0, 8),
                        sublabel: cp.code ?? undefined,
                        entity: cp,
                        hasChildren: false,
                        highlighted: highlighted.has(cp.id),
                      }));
                      edges.push(makeEdge(floor.id, cp.id, "floor-cp"));
                      cpY += NODE_VGAP;
                    }
                  }
                  floorY += fSubtree * NODE_VGAP;
                }
              }
              buildingY += bSubtree * NODE_VGAP;
            }
            // Site-level orphan locations — location rows where
            // `floor_id IS NULL` (data state seen on the demo tenant: site
            // has 10 locations all without floor assignment). Without this
            // branch they vanish from the graph entirely and any sibling site
            // looks identical to this one. Attach them under a synthetic
            // 「（未指定樓層）」 floor hung directly off the site.
            const orphanLocations = locationsByFloor.get(`__site:${site.id}`) ?? [];
            if (orphanLocations.length > 0) {
              const synthFloorId = `__floor:site:${site.id}`;
              const synthExpanded = isExpanded(synthFloorId);
              const fSubtree = synthExpanded ? orphanLocations.length : 1;
              const fCenter = buildingY + (fSubtree - 1) * NODE_VGAP / 2;
              nodes.push(makeNode("floor", synthFloorId, LANE_X.floor, fCenter, {
                kind: "floor",
                label: "（未指定樓層）",
                sublabel: synthExpanded
                  ? `${orphanLocations.length} 個地點`
                  : collapsedSuffix(orphanLocations.length, "個地點") ?? undefined,
                entity: { synthetic: true, site_id: site.id, count: orphanLocations.length },
                hasChildren: true,
                highlighted: false,
              }));
              edges.push(makeEdge(site.id, synthFloorId, "site-orphan-floor"));
              if (synthExpanded) {
                let cpY = buildingY;
                for (const cp of orphanLocations) {
                  nodes.push(makeNode("location", cp.id, LANE_X.location, cpY, {
                    kind: "location",
                    label: cp.label ?? cp.code ?? cp.id.slice(0, 8),
                    sublabel: cp.code ?? undefined,
                    entity: cp,
                    hasChildren: false,
                    highlighted: highlighted.has(cp.id),
                  }));
                  edges.push(makeEdge(synthFloorId, cp.id, "floor-cp"));
                  cpY += NODE_VGAP;
                }
              }
              buildingY += fSubtree * NODE_VGAP;
            }
          }
          siteY += siteSubtree * NODE_VGAP;
        }
      }
      nextClientY += clientSubtreeHeight * NODE_VGAP;
    }
  }

  // Routes overlay — each route is its own node in the rightmost lane;
  // when expanded the edges connect to every location it visits with
  // a distinct dashed style + colour so they don't blur into the
  // structural tree edges.
  if (opts.showRoutes) {
    let routeY = 0;
    for (const route of d.routes) {
      nodes.push(makeNode("route", route.id, LANE_X.route, routeY, {
        kind: "route",
        label: route.name,
        sublabel: `${route.location_ids.length} 站 · ${route.sequence_strict ? "強制順序" : "自由順序"}`,
        entity: route,
      }));
      for (let i = 0; i < route.location_ids.length; i++) {
        const cpId = route.location_ids[i];
        edges.push({
          id: `route-${route.id}-${cpId}-${i}`,
          source: route.id,
          target: cpId,
          type: "default",
          animated: false,
          style: { stroke: "rgba(167, 139, 250, 0.55)", strokeWidth: 1.5, strokeDasharray: "5 3" },
        });
      }
      routeY += NODE_VGAP;
    }
  }

  // AM-5c — focus filter. Visible = focused ids ∪ all ancestors. We
  // post-filter the laid-out tree (keeping all original x/y positions)
  // and rely on React Flow's `fitView` to auto-zoom into the surviving
  // nodes. Edges follow the same gating (both endpoints visible).
  if (focusSet && focusSet.size > 0) {
    const parents = buildParentMap(d);
    const visible = new Set<string>();
    visible.add(d.tenant.id);
    for (const id of focusSet) {
      visible.add(id);
      let cur: string | undefined = parents.get(id);
      while (cur) {
        if (!cur.startsWith("__")) visible.add(cur);
        cur = parents.get(cur);
      }
    }
    const visibleNodes = nodes.filter((n) => visible.has(n.id));
    const visibleEdges = edges.filter((e) => visible.has(e.source) && visible.has(e.target));
    return { nodes: visibleNodes, edges: visibleEdges };
  }

  return { nodes, edges };
}

function group<T, K>(items: T[], keyOf: (t: T) => K): Map<K, T[]> {
  const m = new Map<K, T[]>();
  for (const it of items) {
    const k = keyOf(it);
    const arr = m.get(k) ?? [];
    arr.push(it);
    m.set(k, arr);
  }
  return m;
}

function checkOrphanFloorBucket(
  floorsByBuilding: Map<string, TopologyFloor[]>,
  synthKey: string,
): TopologyFloor[] {
  return floorsByBuilding.get(synthKey) ?? [];
}

interface SubtreeCtx {
  buildingsBySite: Map<string, TopologyBuilding[]>;
  floorsByBuilding: Map<string, TopologyFloor[]>;
  locationsByFloor: Map<string, TopologyLocation[]>;
  /** When a parent isn't expanded, its subtree height collapses to 1
   *  (just the parent node itself). Threaded down through the recursion
   *  so layout calcs match what `buildGraph` actually emits. */
  isExpanded: (id: string) => boolean;
}

function subtreeHeight(items: TopologySite[], ctx: SubtreeCtx): number {
  let h = 0;
  for (const s of items) {
    // If site is collapsed, it contributes a single row regardless of
    // how many buildings / floors / locations it has underneath.
    if (!ctx.isExpanded(s.id)) { h += 1; continue; }
    const buildings = ctx.buildingsBySite.get(s.id) ?? [];
    if (buildings.length === 0) {
      // floors directly under site
      const floors = ctx.floorsByBuilding.get(`__site:${s.id}`) ?? [];
      if (floors.length === 0) { h += 1; }
      for (const f of floors) {
        if (!ctx.isExpanded(f.id)) { h += 1; continue; }
        const cps = ctx.locationsByFloor.get(f.id) ?? [];
        h += Math.max(cps.length, 1);
      }
    } else {
      for (const b of buildings) {
        h += subtreeHeightFromBuilding(b.id, ctx);
      }
      const orphan = ctx.floorsByBuilding.get(`__site:${s.id}`) ?? [];
      for (const f of orphan) {
        if (!ctx.isExpanded(f.id)) { h += 1; continue; }
        const cps = ctx.locationsByFloor.get(f.id) ?? [];
        h += Math.max(cps.length, 1);
      }
    }
    // Site-level orphan locations — synth floor's row. When the synth
    // floor is collapsed it still contributes 1 row for the synth floor
    // node itself; when expanded it contributes one row per location.
    const siteOrphanCps = ctx.locationsByFloor.get(`__site:${s.id}`) ?? [];
    if (siteOrphanCps.length > 0) {
      const synthFloorId = `__floor:site:${s.id}`;
      h += ctx.isExpanded(synthFloorId) ? siteOrphanCps.length : 1;
    }
  }
  return h;
}

function subtreeHeightFromBuilding(buildingId: string, ctx: SubtreeCtx): number {
  // Synth bucket (__site:<id>) is treated as always-expanded since it
  // has no header node of its own to collapse.
  if (!buildingId.startsWith("__site:") && !ctx.isExpanded(buildingId)) return 1;
  const floors = ctx.floorsByBuilding.get(buildingId) ?? [];
  if (floors.length === 0) return 1;
  let h = 0;
  for (const f of floors) {
    if (!ctx.isExpanded(f.id)) { h += 1; continue; }
    const cps = ctx.locationsByFloor.get(f.id) ?? [];
    h += Math.max(cps.length, 1);
  }
  return h;
}

function makeNode(kind: NodeKind, id: string, x: number, y: number, data: NodeData): Node {
  // All edges flow left-to-right (tenant → client → … → route → cp),
  // so every node's source handle is on the right and target on the
  // left. No more route-specific override now that the route lane
  // sits left of the location lane.
  return {
    id,
    position: { x, y },
    data: data as unknown as Record<string, unknown>,
    type: "default",
    style: nodeStyle(kind, { highlighted: !!data.highlighted }),
    sourcePosition: "right" as const,
    targetPosition: "left" as const,
  } as Node;
}

function makeEdge(source: string, target: string, _kind: string): Edge {
  return {
    id: `${source}-${target}`,
    source, target,
    type: "default",
    style: { stroke: "rgba(255,255,255,0.18)", strokeWidth: 1 },
  };
}

function nodeStyle(
  kind: NodeKind,
  opts: { highlighted: boolean } = { highlighted: false },
): React.CSSProperties {
  const colour = nodeColor(kind);
  return {
    background: "rgba(15,23,42,0.85)",
    border: `1.5px solid ${colour}`,
    color: "#e2e8f0",
    fontSize: 11,
    padding: "6px 10px",
    borderRadius: 8,
    minWidth: 120,
    // AM-2 / AM-3: when a node is in the URL ?highlight= set (typically
    // because a plan was just applied that touched it), draw an accent
    // ring + amber glow so the operator's eye lands there fast.
    ...(opts.highlighted
      ? {
          boxShadow: "0 0 0 3px rgba(251, 191, 36, 0.7), 0 0 18px rgba(251, 191, 36, 0.45)",
        }
      : {}),
  };
}

function nodeColor(kind: NodeKind): string {
  switch (kind) {
    case "tenant":     return "#facc15"; // yellow-400
    case "client":     return "#60a5fa"; // blue-400
    case "site":       return "#34d399"; // emerald-400
    case "building":   return "#a78bfa"; // violet-400
    case "floor":      return "#f472b6"; // pink-400
    case "location": return "#06b6d4"; // cyan-500
    case "route":      return "#fb923c"; // orange-400
  }
}

// ─── info panel ────────────────────────────────────────────────────────

function InfoPanel({
  node, topology, onClose,
}: {
  node: NodeData;
  topology: TopologyResponse;
  onClose: () => void;
}): React.ReactElement {
  return (
    <div className="absolute top-3 right-3 w-80 max-w-[calc(100%-1.5rem)] rounded-xl border border-border bg-bg-raised/85 backdrop-blur-md shadow-xl p-4 z-10">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="inline-flex items-center gap-2 min-w-0">
          {kindIcon(node.kind)}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary truncate">{node.label}</p>
            <p className="text-2xs text-text-muted">{kindLabel(node.kind)}{node.sublabel ? ` · ${node.sublabel}` : ""}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary text-xs">關閉</button>
      </div>
      <div className="text-xs text-text-secondary space-y-1.5">
        {renderInfoBody(node, topology)}
      </div>
    </div>
  );
}

function renderInfoBody(node: NodeData, t: TopologyResponse): React.ReactNode {
  switch (node.kind) {
    case "tenant": {
      return (
        <>
          <Field k="客戶數" v={`${t.clients.length}`} />
          <Field k="案場數" v={`${t.sites.length}`} />
          <Field k="樓層數" v={`${t.floors.length}`} />
          <Field k="地點數" v={`${t.locations.length}`} />
          <Field k="路線數" v={`${t.routes.length}`} />
          <Field k="任務數" v={`${t.tasks.length}`} />
        </>
      );
    }
    case "client": {
      const c = node.entity as TopologyClient;
      return (
        <>
          <Field k="狀態" v={c.status === "active" ? "啟用" : "停用"} />
          <Field k="案場數" v={`${c.site_count}`} />
          {c.contact_name && <Field k="聯絡人" v={c.contact_name} />}
        </>
      );
    }
    case "site": {
      const s = node.entity as TopologySite;
      return (
        <>
          <Field k="客戶" v={s.client_name ?? "—"} />
          {s.address && <Field k="地址" v={s.address} />}
          <Field k="狀態" v={s.status === "active" ? "啟用" : "停用"} />
          <Field k="風險" v={s.risk_level === "high" ? "高" : s.risk_level === "medium" ? "中" : "低"} />
          <Field k="地點" v={`${s.location_count} 個`} />
        </>
      );
    }
    case "building": {
      const b = node.entity as TopologyBuilding;
      const floors = t.floors.filter((f) => f.building_id === b.id).length;
      return (
        <>
          <Field k="樓層數" v={`${floors}`} />
          <Field k="顯示順序" v={`${b.display_order}`} />
        </>
      );
    }
    case "floor": {
      const f = node.entity as TopologyFloor;
      const cps = t.locations.filter((c) => c.floor_id === f.id).length;
      return (
        <>
          <Field k="樓層代號" v={f.name} />
          {f.display_label && <Field k="顯示名稱" v={f.display_label} />}
          <Field k="序號" v={`${f.ordinal}`} />
          <Field k="地點" v={`${cps} 個`} />
        </>
      );
    }
    case "location": {
      const cp = node.entity as TopologyLocation;
      const onRoutes = t.routes.filter((r) => r.location_ids.includes(cp.id));
      const assignments = t.assignments.filter((a) => a.location_id === cp.id);
      return (
        <>
          {cp.code && <Field k="代碼" v={cp.code} />}
          <Field k="狀態" v={cp.status === "active" ? "啟用" : "停用"} />
          <Field k="風險" v={cp.risk_level === "high" ? "高" : cp.risk_level === "medium" ? "中" : "低"} />
          <Field k="NTAG" v={cp.ntag_bound ? "已綁定" : "未綁定"} />
          {onRoutes.length > 0 && (
            <Field k="所屬路線" v={onRoutes.map((r) => r.name).join("、")} />
          )}
          {assignments.length > 0 && (
            <Field k="派發任務" v={`${assignments.length} 筆`} />
          )}
        </>
      );
    }
    case "route": {
      const r = node.entity as TopologyRoute;
      return (
        <>
          <Field k="順序" v={r.sequence_strict ? "強制" : "自由"} />
          <Field k="站數" v={`${r.location_ids.length}`} />
        </>
      );
    }
  }
}

function Field({ k, v }: { k: string; v: string }): React.ReactElement {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-text-muted">{k}</span>
      <span className="text-text-primary text-right">{v}</span>
    </div>
  );
}

function kindIcon(k: NodeKind): React.ReactElement {
  const cls = "h-4 w-4";
  switch (k) {
    case "tenant":     return <Building2 className={cls} style={{ color: nodeColor(k) }} />;
    case "client":     return <UsersIcon className={cls} style={{ color: nodeColor(k) }} />;
    case "site":       return <MapPin className={cls} style={{ color: nodeColor(k) }} />;
    case "building":   return <Building2 className={cls} style={{ color: nodeColor(k) }} />;
    case "floor":      return <Layers className={cls} style={{ color: nodeColor(k) }} />;
    case "location": return <ScanLine className={cls} style={{ color: nodeColor(k) }} />;
    case "route":      return <RouteIcon className={cls} style={{ color: nodeColor(k) }} />;
  }
}

function kindLabel(k: NodeKind): string {
  switch (k) {
    case "tenant":     return "帳戶";
    case "client":     return "終端客戶";
    case "site":       return "案場";
    case "building":   return "建築物";
    case "floor":      return "樓層";
    case "location": return "地點";
    case "route":      return "路線";
  }
}
